import {
  InquiryRequest,
  isAllInquiryParams,
  useUploadInquiry,
} from '@/API/serviceAPI'
import PhoneInput from '@/components/form/PhoneInput'
import UserInfoConsent from '@/components/user/UserInfoConsent'
import {
  checkEmailValidation,
  checkPhoneNumberVaildation,
  ToastUtil,
} from '@/util/util'
import {
  chakra,
  Flex,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useMediaQuery,
  Button,
  useToast,
  UseToastOptions,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const Contact = () => {
  const toast = useToast()
  const { t } = useTranslation()
  const [isMobile] = useMediaQuery('(max-width: 768px)')
  const [isConsent, setIsConsent] = useState<boolean>(false)

  const { mutate: uploadInquiry, isSuccess: isUploadInquirySuccess } =
    useUploadInquiry()

  const toggleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const target = e.target as HTMLFormElement
    const inputs = Array.from(target.elements) as Array<HTMLInputElement>
    const detailTextArea = target.elements[5] as HTMLTextAreaElement
    const params: Partial<InquiryRequest> = {}

    inputs.map((input) => {
      if (input.tagName === 'INPUT' && !input.id.includes('field')) {
        const id = input.id as keyof InquiryRequest
        params[id] = input.value
      }
    })

    if (detailTextArea.value) {
      params['detail'] = detailTextArea.value
    } else {
      toast(
        ToastUtil(
          t('contact.message.detailRequired'),
          'warning',
          t('contact.message.detailRequiredDesc'),
        ) as UseToastOptions,
      )
      return
    }

    if (isAllInquiryParams(params)) {
      const isVaildEmail = checkEmailValidation(params.email)
      const isVaildPhoneNumber = checkPhoneNumberVaildation(params.phone)
      if (isVaildEmail && isVaildPhoneNumber && isConsent) {
        uploadInquiry(params)
      } else if (!isVaildEmail) {
        toast(
          ToastUtil(
            t('contact.message.emailError'),
            'warning',
            t('contact.message.emailErrorDesc'),
          ) as UseToastOptions,
        )
      } else if (!isVaildPhoneNumber) {
        toast(
          ToastUtil(
            t('contact.message.phoneError'),
            'warning',
            t('contact.message.phoneErrorDesc'),
          ) as UseToastOptions,
        )
      } else if (!isConsent) {
        toast(
          ToastUtil(
            t('contact.message.consentRequired'),
            'warning',
            t('contact.message.consentRequiredDesc'),
          ) as UseToastOptions,
        )
      } else {
        toast(
          ToastUtil(
            t('contact.message.emptyFieldError'),
            'warning',
            t('contact.message.emptyFieldErrorDesc'),
          ) as UseToastOptions,
        )
      }
    }
  }
  const returnInputForm = () => {
    const eachFormId = [
      {
        name: t('contact.form.companyName'),
        id: 'company_name',
      },
      {
        name: t('contact.form.contactName'),
        id: 'contact_name',
      },
      { name: t('contact.form.email'), id: 'email' },
      { name: t('contact.form.phone'), id: 'phone' },
      { name: t('contact.form.title'), id: 'title' },
    ]
    return eachFormId.map((eachData, idx) => {
      return (
        <Flex
          flex={1}
          minWidth={isMobile ? 'auto' : '450px'}
          flexDirection="column"
          key={idx}
          gap={isMobile ? 2 : 4}
          id={`input-group`}
        >
          <FormLabel fontSize={isMobile ? 'md' : '18px'} fontWeight={500}>
            {eachData.name}
          </FormLabel>
          {eachData.id === 'phone' ? 
            <PhoneInput
              isMobile={isMobile}
              id="phone"
            />
                :
              <Input
                id={eachData.id}
                w="auto"
                h={isMobile ? '50px' : '60px'}
                bgColor="#F7F7F7"
                borderRadius={0}
                border="none"
                placeholder={eachData.id === 'phone' ? '010-xxxx-xxxx' : ''}
              />
            }
        </Flex>
      )
    })
  }

  useEffect(() => {
    if (isUploadInquirySuccess) {
      toast(
        ToastUtil(
          t('contact.message.success'),
          'success',
          t('contact.message.successDesc'),
        ) as UseToastOptions,
      )
      
      setTimeout(() => {
        window.scrollTo(0,0)
        window.location.reload();
      }, 500);
    }
  }, [isUploadInquirySuccess, t])

  return (
    <Flex
      id="contact"
      py={isMobile ? 10 : 16}
      px={isMobile ? 6 : '10%'}
      minW="100vw"
      flexDirection={'column'}
    >
      <Flex
        flexDirection="column"
        gap={isMobile ? 4 : 6}
        justifyContent="flex-start"
        w="full"
        maxW={'full'}
        py={6}
      >
        <Flex flexDirection="column" gap={4} pb={2} alignItems={'center'}>
          <Heading color="#004A8D" fontSize={isMobile ? '34px' : '50px'}>
            {t('contact.title')}
          </Heading>
          <Text
            fontSize={isMobile ? 'md' : '22px'}
            color="#787E88"
            fontWeight={500}
            textAlign="center"
            whiteSpace="pre-line"
          >
            {t('contact.subtitle')}
          </Text>
        </Flex>
      </Flex>
      <Flex w="full" py={8} overflowY="auto">
        <chakra.form onSubmit={toggleSubmit}>
          <FormControl
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            gap={8}
          >
            {returnInputForm()}
            <Flex flex="full" w="full" flexDirection="column" gap={2}>
              <FormLabel fontSize={isMobile ? 'md' : '18px'} fontWeight={500}>
                {t('contact.form.detail')}
              </FormLabel>
              <Textarea
                id="detail"
                minH={isMobile ? '300px' : '185px'}
                bgColor="#F7F7F7"
                borderRadius={0}
                border="none"
              />
            </Flex>
            <UserInfoConsent
              isConsent={isConsent}
              setIsConsent={setIsConsent}
            />
            <Flex w="full" justifyContent="center" my={4}>
              <Button
                type="submit"
                w="full"
                h={isMobile ? '50px' : '60px'}
                bgColor="#0D3A65"
                borderRadius={0}
              >
                {t('contact.form.submit')}
              </Button>
            </Flex>
          </FormControl>
        </chakra.form>
      </Flex>
    </Flex>
  )
}

export default Contact
